import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, set, query, orderByChild, equalTo } from 'firebase/database';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { Container, Row, Col, Card, Button, Form, Alert, Modal, ListGroup, Badge, Table, Dropdown } from 'react-bootstrap';
import { Plus, Calendar, Settings, Clock, AlertTriangle, CheckCircle, Check, BarChart3, MoreVertical, Trash2, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BuildingCheckTemplate } from './types';
import { getAuth } from 'firebase/auth';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useUserNames } from '../../Helpers/useUserNames';
import { 
    BuildingCheck, 
    BuildingCheckWithTemplate, 
    CheckCompletion, 
    formatDate, 
    formatInterval, 
    formatDaysUntilDue, 
    getDaysUntilDue, 
    calculateNextDueDate,
    updateActiveCheckWithLastCompletion
} from './buildingChecksUtils';
import './BuildingChecksPage.css';
import { Helmet } from 'react-helmet-async';


const BuildingChecksPage: React.FC = () => {
    const { buildingId } = useParams<{ buildingId: string }>();
    const navigate = useNavigate();
    const auth = getAuth();
    const user = auth.currentUser;
    const { currentBuilding } = useBuilding();
    const { userNames, loadUserNames } = useUserNames();
    const [buildingChecks, setBuildingChecks] = useState<BuildingCheckWithTemplate[]>([]);
    const [availableTemplates, setAvailableTemplates] = useState<BuildingCheckTemplate[]>([]);
    const [completedChecks, setCompletedChecks] = useState<{[templateId: string]: string[]}>({}); // templateId -> array of completion dates
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<BuildingCheckTemplate | null>(null);
    const [selectedStartDate, setSelectedStartDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    
    // Completion modal state
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [checkToComplete, setCheckToComplete] = useState<BuildingCheckWithTemplate | null>(null);
    const [modalAlert, setModalAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    const [completionForm, setCompletionForm] = useState({
        completedAt: '',
        measurementValue: '',
        notes: ''
    });

    // Statistics modal state
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [checkForStats, setCheckForStats] = useState<BuildingCheckWithTemplate | null>(null);
    const [checkResults, setCheckResults] = useState<CheckCompletion[]>([]);
    
    
    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkToDelete, setCheckToDelete] = useState<BuildingCheckWithTemplate | null>(null);

    const db = getDatabase();

    // Load building checks and available templates
    useEffect(() => {
        const actualBuildingId = buildingId || currentBuilding?.key;
        console.log('BuildingChecksPage useEffect triggered, buildingId:', buildingId, 'currentBuilding.key:', currentBuilding?.key, 'actualBuildingId:', actualBuildingId);
        if (actualBuildingId) {
            console.log('Loading building checks and templates for buildingId:', actualBuildingId);
            loadBuildingChecks(actualBuildingId);
            loadAvailableTemplates();
            loadCompletedChecks(actualBuildingId);
        } else {
            console.log('No buildingId provided');
        }
    }, [buildingId, currentBuilding]);

    const loadBuildingChecks = async (actualBuildingId: string) => {
        try {
            console.log('loadBuildingChecks: Starting to load checks for buildingId:', actualBuildingId);
            setLoading(true);
            const checksRef = ref(db, `buildingChecks/active/${actualBuildingId}`);
            console.log('loadBuildingChecks: Firebase ref created:', checksRef.toString());
            
            const checksSnapshot = await get(checksRef);
            console.log('loadBuildingChecks: Firebase snapshot received, exists:', checksSnapshot.exists());
            
            if (checksSnapshot.exists()) {
                const checksData = checksSnapshot.val();
                console.log('loadBuildingChecks: Checks data:', checksData);
                const checks: BuildingCheckWithTemplate[] = [];
                
                // Load template data for each check
                for (const [templateId, checkData] of Object.entries(checksData)) {
                    console.log('loadBuildingChecks: Loading template for templateId:', templateId);
                    const templateRef = ref(db, `buildingChecks/templates/${templateId}`);
                    const templateSnapshot = await get(templateRef);
                    
                    if (templateSnapshot.exists()) {
                        const templateData = templateSnapshot.val();
                        console.log('loadBuildingChecks: Template data loaded:', templateData);
                        checks.push({
                            ...(checkData as BuildingCheck),
                            templateId,
                            template: templateData
                        });
                    } else {
                        console.log('loadBuildingChecks: Template not found for templateId:', templateId);
                    }
                }
                
                console.log('loadBuildingChecks: Final checks array:', checks);
                setBuildingChecks(checks);
            } else {
                console.log('loadBuildingChecks: No checks found for building');
                setBuildingChecks([]);
            }
        } catch (error) {
            console.error('Error loading building checks:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av kontroller' });
        } finally {
            console.log('loadBuildingChecks: Setting loading to false');
            setLoading(false);
        }
    };

    const loadAvailableTemplates = async () => {
        try {
            console.log('loadAvailableTemplates: Starting to load templates');
            const templatesRef = ref(db, 'buildingChecks/templates');
            console.log('loadAvailableTemplates: Firebase ref created:', templatesRef.toString());
            
            const templatesSnapshot = await get(templatesRef);
            console.log('loadAvailableTemplates: Firebase snapshot received, exists:', templatesSnapshot.exists());
            
            if (templatesSnapshot.exists()) {
                const templatesData = templatesSnapshot.val();
                console.log('loadAvailableTemplates: Templates data:', templatesData);
                const templates = Object.values(templatesData) as BuildingCheckTemplate[];
                console.log('loadAvailableTemplates: Processed templates array:', templates);
                setAvailableTemplates(templates);
            } else {
                console.log('loadAvailableTemplates: No templates found');
                setAvailableTemplates([]);
            }
        } catch (error) {
            console.error('Error loading templates:', error);
        }
    };

    const loadCompletedChecks = async (actualBuildingId: string) => {
        try {
            console.log('loadCompletedChecks: Starting to load completed checks for buildingId:', actualBuildingId);
            const resultsRef = ref(db, `buildingChecks/results/${actualBuildingId}`);
            const resultsSnapshot = await get(resultsRef);
            
            if (resultsSnapshot.exists()) {
                const resultsData = resultsSnapshot.val();
                const completed: {[templateId: string]: string[]} = {};
                
                // Process each template's completions
                for (const [templateId, completions] of Object.entries(resultsData)) {
                    if (completions && typeof completions === 'object') {
                        // The keys are the completion dates (YYYY-MM-DD format)
                        completed[templateId] = Object.keys(completions);
                    }
                }
                
                console.log('loadCompletedChecks: Loaded completed checks:', completed);
                setCompletedChecks(completed);
            } else {
                console.log('loadCompletedChecks: No completed checks found');
                setCompletedChecks({});
            }
        } catch (error) {
            console.error('Error loading completed checks:', error);
        }
    };

    const loadCheckResults = async (templateId: string, actualBuildingId: string) => {
        try {
            console.log('loadCheckResults: Loading results for templateId:', templateId);
            const resultsRef = ref(db, `buildingChecks/results/${actualBuildingId}/${templateId}`);
            const resultsSnapshot = await get(resultsRef);
            
            if (resultsSnapshot.exists()) {
                const resultsData = resultsSnapshot.val();
                const results: CheckCompletion[] = [];
                const uniqueUserIds = new Set<string>();
                
                // Process each completion (date as key)
                for (const [date, completionData] of Object.entries(resultsData)) {
                    const completion = completionData as CheckCompletion;
                    results.push({
                        ...completion,
                        completedAt: date // Use the date key as completedAt
                    });
                    
                    // Collect unique user IDs
                    if (completion.completedBy) {
                        uniqueUserIds.add(completion.completedBy);
                    }
                }
                
                // Sort by date (most recent first)
                results.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
                
                console.log('loadCheckResults: Loaded results:', results);
                setCheckResults(results);
                
                // Load user names for all unique users
                await loadUserNames(Array.from(uniqueUserIds));
            } else {
                console.log('loadCheckResults: No results found');
                setCheckResults([]);
            }
        } catch (error) {
            console.error('Error loading check results:', error);
            setCheckResults([]);
        }
    };


    const getAvailableTemplates = (): BuildingCheckTemplate[] => {
        const usedTemplateIds = buildingChecks.map(check => check.templateId);
        return availableTemplates.filter(template => !usedTemplateIds.includes(template.id));
    };

    const getDueDateStatus = (nextDueDate: string, advanceNotice: number) => {
        const today = new Date();
        const dueDate = new Date(nextDueDate);
        const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysUntilDue < 0) {
            return { status: 'overdue', text: 'Försenad', color: 'danger' };
        } else if (daysUntilDue <= advanceNotice) {
            return { status: 'due_soon', text: 'Ska göras inom kort', color: 'warning' };
        } else {
            return { status: 'upcoming', text: 'Kommande', color: 'secondary' };
        }
    };

    const isCheckCompletedToday = (templateId: string) => {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const templateCompletions = completedChecks[templateId] || [];
        return templateCompletions.includes(today);
    };


    const getCompletionCount = (templateId: string) => {
        const templateCompletions = completedChecks[templateId] || [];
        return templateCompletions.length;
    };

    const getLastCompletion = async (templateId: string, actualBuildingId: string) => {
        try {
            const resultsRef = ref(db, `buildingChecks/results/${actualBuildingId}/${templateId}`);
            const resultsSnapshot = await get(resultsRef);
            
            if (resultsSnapshot.exists()) {
                const resultsData = resultsSnapshot.val();
                const completions = Object.entries(resultsData)
                    .map(([date, data]) => ({ date, ...(data as any) }))
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                
                return completions.length > 0 ? completions[0] : null;
            }
            return null;
        } catch (error) {
            console.error('Error loading last completion:', error);
            return null;
        }
    };


    const handleAddCheck = async () => {
        const actualBuildingId = buildingId || currentBuilding?.key;
        if (!selectedTemplate || !selectedStartDate || !user || !actualBuildingId) {
            setAlert({ type: 'danger', message: 'Vänligen välj mall och startdatum' });
            return;
        }

        try {
            setSaving(true);
            const now = new Date().toISOString();
            
            const checkData: BuildingCheck = {
                templateId: selectedTemplate.id,
                startDate: selectedStartDate,
                nextDueDate: selectedStartDate,
                addedBy: user.uid,
                addedTime: now
            };

            const checkRef = ref(db, `buildingChecks/active/${actualBuildingId}/${selectedTemplate.id}`);
            await set(checkRef, checkData);

            setAlert({ type: 'success', message: 'Kontroll tillagd framgångsrikt!' });
            setShowAddModal(false);
            setSelectedTemplate(null);
            setSelectedStartDate('');
            loadBuildingChecks(actualBuildingId);
        } catch (error) {
            console.error('Error adding check:', error);
            setAlert({ type: 'danger', message: 'Fel vid tillägg av kontroll' });
        } finally {
            setSaving(false);
        }
    };



    const openCompletionModal = (check: BuildingCheckWithTemplate) => {
        setCheckToComplete(check);
        setCompletionForm({
            completedAt: new Date().toISOString().split('T')[0], // Default to today
            measurementValue: '',
            notes: ''
        });
        setModalAlert(null); // Clear any previous modal alerts
        setShowCompletionModal(true);
    };

    const openStatsModal = async (check: BuildingCheckWithTemplate) => {
        const actualBuildingId = buildingId || currentBuilding?.key;
        if (!actualBuildingId) return;
        
        setCheckForStats(check);
        setShowStatsModal(true);
        await loadCheckResults(check.templateId, actualBuildingId);
    };

    const handleCompleteCheck = async () => {
        if (!checkToComplete || !user || !actualBuildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        // Validate completion date is not in the future
        const today = new Date().toISOString().split('T')[0];
        if (completionForm.completedAt > today) {
            setModalAlert({ type: 'danger', message: 'Slutförandedatum kan inte vara i framtiden.' });
            return;
        }

        // Validate measurement if required
        if (checkToComplete.template.takesMeasurement && (!completionForm.measurementValue || completionForm.measurementValue.trim() === '')) {
            setModalAlert({ type: 'danger', message: 'Mätvärde krävs för denna kontroll. Vänligen ange ett värde.' });
            return;
        }

        try {
            setSaving(true);
            
            // Check if a completion already exists for this date
            const existingCompletionRef = ref(db, `buildingChecks/results/${actualBuildingId}/${checkToComplete.templateId}/${completionForm.completedAt}`);
            const existingSnapshot = await get(existingCompletionRef);
            
            if (existingSnapshot.exists()) {
                setModalAlert({ 
                    type: 'danger', 
                    message: `En kontroll har redan registrerats för ${formatDate(completionForm.completedAt)}. Det går inte att registrera flera kontroller samma dag.` 
                });
                setSaving(false);
                return;
            }
            
            const now = new Date().toISOString();
            
            // Calculate next due date
            const nextDueDate = calculateNextDueDate(
                checkToComplete.template, 
                completionForm.completedAt, 
                checkToComplete.nextDueDate
            );

            // Prepare completion data
            const completionData: any = {
                templateId: checkToComplete.templateId,
                completedBy: user.uid,
                completedAt: completionForm.completedAt,
                submittedAt: now,
                actualDueDate: checkToComplete.nextDueDate,
                nextDueDate: nextDueDate
            };

            // Only add notes if it has a value
            if (completionForm.notes && completionForm.notes.trim()) {
                completionData.notes = completionForm.notes.trim();
            }

            // Add measurement data if applicable
            if (checkToComplete.template.takesMeasurement && completionForm.measurementValue) {
                const measurementValue = parseFloat(completionForm.measurementValue);
                const range = checkToComplete.template.takesMeasurement.acceptableRange;
                
                let withinRange = true;
                if (range) {
                    if (range.min !== undefined && measurementValue < range.min) withinRange = false;
                    if (range.max !== undefined && measurementValue > range.max) withinRange = false;
                }

                completionData.measurement = {
                    value: measurementValue,
                    unit: checkToComplete.template.takesMeasurement.metric,
                    withinRange: withinRange
                };
            }

            // Save completion to results using date as key: results/{buildingID}/{templateID}/YYYY-MM-DD
            const completionRef = ref(db, `buildingChecks/results/${actualBuildingId}/${checkToComplete.templateId}/${completionForm.completedAt}`);
            await set(completionRef, completionData);

            // Update active check with last completion data
            const measurement = completionData.measurement ? {
                value: completionData.measurement.value,
                unit: completionData.measurement.unit,
                withinRange: completionData.measurement.withinRange
            } : undefined;

            await updateActiveCheckWithLastCompletion(
                actualBuildingId,
                checkToComplete.templateId,
                completionForm.completedAt,
                nextDueDate,
                measurement
            );

            setAlert({ type: 'success', message: 'Kontroll markerad som klar!' });
            setShowCompletionModal(false);
            setCheckToComplete(null);
            setCompletionForm({ completedAt: '', measurementValue: '', notes: '' });
            loadBuildingChecks(actualBuildingId);
            loadCompletedChecks(actualBuildingId);
        } catch (error) {
            console.error('Error completing check:', error);
            setAlert({ type: 'danger', message: 'Fel vid markering av kontroll som klar' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCheck = async () => {
        if (!checkToDelete || !actualBuildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        try {
            setSaving(true);
            
            // Remove the check from active checks
            const activeCheckRef = ref(db, `buildingChecks/active/${actualBuildingId}/${checkToDelete.templateId}`);
            await set(activeCheckRef, null);

            setAlert({ type: 'success', message: 'Kontroll borttagen framgångsrikt!' });
            setShowDeleteModal(false);
            setCheckToDelete(null);
            loadBuildingChecks(actualBuildingId);
        } catch (error) {
            console.error('Error deleting check:', error);
            setAlert({ type: 'danger', message: 'Fel vid borttagning av kontroll' });
        } finally {
            setSaving(false);
        }
    };

    const actualBuildingId = buildingId || currentBuilding?.key;
    console.log('BuildingChecksPage render - loading:', loading, 'buildingId:', buildingId, 'currentBuilding.key:', currentBuilding?.key, 'actualBuildingId:', actualBuildingId, 'buildingChecks:', buildingChecks.length, 'availableTemplates:', availableTemplates.length);

    if (!actualBuildingId && !loading) {
        console.log('BuildingChecksPage: No building ID available');
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <Alert variant="danger">
                        <h4>Ingen byggnad vald</h4>
                        <p>Vänligen navigera till en byggnad för att visa kontroller.</p>
                    </Alert>
                </div>
            </Container>
        );
    }

    if (loading) {
        console.log('BuildingChecksPage: Showing loading spinner');
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <p className="mt-2">Laddar kontroller...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container className="mt-4 building-checks-container">
            <Helmet>
                <title>{currentBuilding?.summary?.name ? `Kontroller - ${currentBuilding.summary.name} - Molnfastigheter` : 'Kontroller - Molnfastigheter'}</title>
            </Helmet>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Kontroller för byggnad</h2>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/settings?tab=building-checks')}
                    >
                        <Settings size={16} className="me-1" />
                        Hantera kontrollmallar
                    </Button>
                    <Button
                        variant={getAvailableTemplates().length === 0 ? "secondary" : "primary"}
                        onClick={() => setShowAddModal(true)}
                        disabled={getAvailableTemplates().length === 0}
                        title={getAvailableTemplates().length === 0 ? "Alla tillgängliga mallar är redan tillagda" : "Lägg till ny kontroll"}
                    >
                        <Plus size={16} className="me-1" />
                        {getAvailableTemplates().length === 0 ? "Inga mallar tillgängliga" : "Lägg till kontroll"}
                    </Button>
                </div>
            </div>

            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            {buildingChecks.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <Clock size={48} className="text-muted mb-3" />
                        <h5>Inga kontroller tillagda</h5>
                        <p className="text-muted">
                            Lägg till kontroller från tillgängliga mallar för att börja spåra underhållsaktiviteter.
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => setShowAddModal(true)}
                            disabled={getAvailableTemplates().length === 0}
                        >
                            <Plus size={16} className="me-1" />
                            Lägg till första kontrollen
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <Card className="shadow-sm building-checks-table-card" style={{ borderRadius: '0.5rem' }}>
                    <Card.Body className="p-4 bg-white" style={{ borderRadius: '0.5rem' }}>
                        <Table responsive striped hover>
                            <thead>
                                <tr>
                                    <th>Kontroll</th>
                                    <th>Förfallodatum</th>
                                    <th>Senast genomförd</th>
                                    <th>Intervall</th>
                                    <th>Åtgärder</th>
                                </tr>
                            </thead>
                    <tbody>
                        {buildingChecks
                            .sort((a, b) => {
                                const daysA = getDaysUntilDue(a.nextDueDate);
                                const daysB = getDaysUntilDue(b.nextDueDate);
                                return daysA - daysB; // Sort by days until due (ascending)
                            })
                            .map((check) => {
                            const dueStatus = getDueDateStatus(check.nextDueDate, check.template.advanceNotice);
                            const daysText = formatDaysUntilDue(check.nextDueDate);
                            const isCompletedToday = isCheckCompletedToday(check.templateId);
                            
                            return (
                                <tr key={check.templateId}>
                                    <td>
                                        <strong>{check.template.name}</strong>
                                        {check.template.estimatedTime > 0 && (
                                            <div className="small text-muted">
                                                {check.template.estimatedTime} min
                                            </div>
                                        )}
                                        {check.template.takesMeasurement && (
                                            <div className="small text-muted mt-1">
                                                <strong>Mätvärde:</strong> {check.template.takesMeasurement.metric}
                                                {check.template.takesMeasurement.acceptableRange && (
                                                    <span>
                                                        {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                            <span> ({check.template.takesMeasurement.acceptableRange.min} - {check.template.takesMeasurement.acceptableRange.max})</span>
                                                        )}
                                                        {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max === undefined && (
                                                            <span> (min: {check.template.takesMeasurement.acceptableRange.min})</span>
                                                        )}
                                                        {check.template.takesMeasurement.acceptableRange.min === undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                            <span> (max: {check.template.takesMeasurement.acceptableRange.max})</span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div>{formatDate(check.nextDueDate)}</div>
                                        <div className={`small ${
                                            getDaysUntilDue(check.nextDueDate) < 0 ? 'text-danger' : 
                                            getDaysUntilDue(check.nextDueDate) === 0 ? 'text-warning' : 
                                            getDaysUntilDue(check.nextDueDate) <= check.template.advanceNotice ? 'text-danger' : 
                                            'text-muted'
                                        }`}>
                                            {daysText}
                                        </div>
                                    </td>
                                    <td>
                                        {check.lastCompletedDate ? (
                                            <div>
                                                <div className="small d-flex align-items-center">
                                                    {formatDate(check.lastCompletedDate)}
                                                    {check.lastCompletedDate === new Date().toISOString().split('T')[0] && (
                                                        <Check size={16} className="text-success ms-1" />
                                                    )}
                                                </div>
                                                {check.template.takesMeasurement && check.lastMeasurement && (
                                                    <div className="small text-muted">
                                                        {check.lastMeasurement.value} {check.lastMeasurement.unit}
                                                        {!check.lastMeasurement.withinRange && (
                                                            <span className="text-danger ms-1">⚠</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted small">Aldrig genomförd</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="small">{formatInterval(check.template.interval)}</div>
                                    </td>
                                    <td>
                                        <Dropdown>
                                            <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${check.templateId}`}>
                                                <MoreVertical size={16} />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => openCompletionModal(check)}>
                                                    <CheckCircle size={16} className="me-2" />
                                                    Markera som utförd
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => openStatsModal(check)}>
                                                    <BarChart3 size={16} className="me-2" />
                                                    Visa statistik
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                                <Dropdown.Item 
                                                    onClick={() => {
                                                        setCheckToDelete(check);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className="text-danger"
                                                >
                                                    <Trash2 size={16} className="me-2" />
                                                    Radera
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* Add Check Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Lägg till kontroll</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {getAvailableTemplates().length === 0 ? (
                        <div className="text-center py-4">
                            <p>Alla tillgängliga mallar är redan tillagda till denna byggnad.</p>
                            <Button
                                variant="outline-primary"
                                onClick={() => navigate('/settings?tab=building-checks')}
                            >
                                <Settings size={16} className="me-1" />
                                Hantera kontrollmallar
                            </Button>
                        </div>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Välj kontrollmall</Form.Label>
                                <Form.Select
                                    value={selectedTemplate?.id || ''}
                                    onChange={(e) => {
                                        const template = getAvailableTemplates().find(t => t.id === e.target.value);
                                        setSelectedTemplate(template || null);
                                    }}
                                >
                                    <option value="">Välj mall...</option>
                                    {getAvailableTemplates().map(template => (
                                        <option key={template.id} value={template.id}>
                                            {template.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {selectedTemplate && (
                                <>
                                    <div className="mb-3">
                                        <h6>Mallinformation</h6>
                                        <p className="text-muted">{selectedTemplate.description}</p>
                                        <div className="small text-muted">
                                            <div>Intervall: {formatInterval(selectedTemplate.interval)}</div>
                                            {selectedTemplate.estimatedTime > 0 && (
                                                <div>Beräknad tid: {selectedTemplate.estimatedTime} min</div>
                                            )}
                                        </div>
                                    </div>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Välj första startdatum för denna kontroll</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={selectedStartDate}
                                            onChange={(e) => setSelectedStartDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddCheck}
                        disabled={!selectedTemplate || !selectedStartDate || saving}
                    >
                        {saving ? 'Lägger till...' : 'Lägg till kontroll'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Completion Modal */}
            <Modal show={showCompletionModal} onHide={() => setShowCompletionModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Markera kontroll som klar</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalAlert && (
                        <Alert variant={modalAlert.type} className="mb-3">
                            {modalAlert.message}
                        </Alert>
                    )}
                    {checkToComplete && (
                        <>
                            <div className="mb-3">
                                <h6>Kontrollinformation</h6>
                                <p><strong>Namn:</strong> {checkToComplete.template.name}</p>
                                <p><strong>Beskrivning:</strong> {checkToComplete.template.description}</p>
                                <p><strong>Förfallodatum:</strong> {formatDate(checkToComplete.nextDueDate)}</p>
                            </div>

                            {/* Instructional Attachments */}
                            {checkToComplete.template.instructionalAttachments && Object.keys(checkToComplete.template.instructionalAttachments).length > 0 && (
                                <div className="mb-3">
                                    <h6>Instruktionsbilagor</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {Object.entries(checkToComplete.template.instructionalAttachments).map(([dbKey, attachment]) => (
                                            <Button
                                                key={dbKey}
                                                variant="outline-primary"
                                                size="sm"
                                                className="d-flex align-items-center"
                                                onClick={() => {
                                                    // Create download URL from storage path
                                                    const storage = getStorage();
                                                    const fileRef = storageRef(storage, attachment.storagePath);
                                                    getDownloadURL(fileRef).then((url) => {
                                                        // Open in new tab
                                                        window.open(url, '_blank');
                                                    }).catch((error) => {
                                                        console.error('Error getting download URL:', error);
                                                        window.alert('Fel vid hämtning av fil');
                                                    });
                                                }}
                                            >
                                                <FileText size={14} className="me-1" />
                                                {attachment.key}
                                            </Button>
                                        ))}
                                    </div>
                                    <small className="text-muted">Klicka på en bilaga för att öppna den i en ny flik</small>
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>När genomfördes kontrollen? *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={completionForm.completedAt}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, completedAt: e.target.value }))}
                                    required
                                />
                                {completionForm.completedAt && checkToComplete && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Nästa förfallodatum beräknat på valt datum: {calculateNextDueDate(
                                                checkToComplete.template, 
                                                completionForm.completedAt, 
                                                checkToComplete.nextDueDate
                                            )}
                                        </small>
                                    </div>
                                )}
                            </Form.Group>

                            {checkToComplete.template.takesMeasurement && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Mätvärde ({checkToComplete.template.takesMeasurement.metric}) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="any"
                                        value={completionForm.measurementValue}
                                        onChange={(e) => setCompletionForm(prev => ({ ...prev, measurementValue: e.target.value }))}
                                        placeholder="Ange mätvärde"
                                        required
                                    />
                                    {checkToComplete.template.takesMeasurement.acceptableRange && (
                                        <Form.Text className="text-muted">
                                            Acceptabelt intervall: 
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> {checkToComplete.template.takesMeasurement.acceptableRange.min} - {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max === undefined && (
                                                <span> Min: {checkToComplete.template.takesMeasurement.acceptableRange.min}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min === undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> Max: {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Anteckningar</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={completionForm.notes}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Valfria anteckningar om genomförandet..."
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompletionModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleCompleteCheck}
                        disabled={!completionForm.completedAt || saving}
                    >
                        {saving ? 'Sparar...' : 'Markera som klar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Statistics Modal */}
            <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <BarChart3 size={20} className="me-2" />
                        Statistik och loggar - {checkForStats?.template.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkForStats && (
                        <>
                            {checkResults.length === 0 ? (
                                <div className="text-center py-4">
                                    <BarChart3 size={48} className="text-muted mb-3" />
                                    <h5>Inga resultat ännu</h5>
                                    <p className="text-muted">
                                        Denna kontroll har inte genomförts ännu. Resultat och statistik kommer att visas här efter första genomförandet.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Chart for measurements */}
                                    {checkForStats.template.takesMeasurement && checkResults.some(r => r.measurement) && (
                                        <div className="mb-4">
                                            <h6>Mätvärden över tid</h6>
                                            <div style={{ height: '300px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={checkResults
                                                            .filter(r => r.measurement)
                                                            .map(r => ({
                                                                date: new Date(r.completedAt).toLocaleDateString('sv-SE'),
                                                                value: r.measurement?.value || 0,
                                                                withinRange: r.measurement?.withinRange || false
                                                            }))
                                                            .reverse() // Show oldest first
                                                        }
                                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis 
                                                            dataKey="date" 
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={80}
                                                        />
                                                        <YAxis 
                                                            label={{ value: checkForStats.template.takesMeasurement?.metric || '', angle: -90, position: 'insideLeft' }}
                                                        />
                                                        <Tooltip 
                                                            formatter={(value: any, name: string) => [
                                                                `${value} ${checkForStats.template.takesMeasurement?.metric || ''}`,
                                                                'Värde'
                                                            ]}
                                                            labelFormatter={(label) => `Datum: ${label}`}
                                                        />
                                                        <Bar 
                                                            dataKey="value" 
                                                            fill="#8884d8"
                                                            name="Mätvärde"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {/* Results table */}
                                    <div>
                                        <h6>Alla genomföranden ({checkResults.length})</h6>
                                        <Table responsive striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Datum</th>
                                                    <th>Genomförd av</th>
                                                    {checkForStats.template.takesMeasurement && <th>Mätvärde</th>}
                                                    <th>Anteckningar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {checkResults.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>{formatDate(result.completedAt)}</td>
                                                        <td>
                                                            <div>{userNames[result.completedBy] || 'Okänd användare'}</div>
                                                            <small className="text-muted">
                                                                {new Date(result.submittedAt).toLocaleDateString('sv-SE')} {new Date(result.submittedAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                            </small>
                                                        </td>
                                                        {checkForStats.template.takesMeasurement && (
                                                            <td>
                                                                {result.measurement ? (
                                                                    <div>
                                                                        <span className={result.measurement.withinRange ? 'text-success' : 'text-danger'}>
                                                                            {result.measurement.value} {result.measurement.unit}
                                                                        </span>
                                                                        {!result.measurement.withinRange && (
                                                                            <Badge bg="danger" className="ms-1 small">Utanför gränsvärde</Badge>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td>
                                                            {result.notes ? (
                                                                <small>{result.notes}</small>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatsModal(false)}>
                        Stäng
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta radering</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkToDelete && (
                        <>
                            <p>Är du säker på att du vill radera kontrollen <strong>"{checkToDelete.template.name}"</strong>?</p>
                            <Alert variant="info" className="mt-3">
                                <strong>Obs:</strong> Eventuell historik kommer att finnas kvar även efter radering.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteCheck}
                        disabled={saving}
                    >
                        {saving ? 'Raderar...' : 'Radera'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default BuildingChecksPage;
